/**
 * VolumeWindowControl — mode toggle + focus-window slider.
 *
 * Rendered on top of the viewport grid when the viewer is in a volume mode
 * (MPR / 3D / MIP) AND the active series is larger than the GPU budget. For
 * small studies the whole volume fits and this UI stays hidden.
 *
 *  Overview — stride-skip covering the whole study at reduced fidelity
 *  Focus    — contiguous full-res window the user drags with a dual-handle
 *             slider, then presses Apply to rebuild the volume
 */
import { useEffect, useState } from 'react';
import { StudyData, SeriesData } from '../cornerstone/init';
import { useViewerStore, VolumeBuildMode } from '../stores/viewerStore';
import { estimateVolumeBudget } from '../cornerstone/volumeBudget';
import styles from './VolumeWindowControl.module.css';

interface Props {
  studyData: StudyData | null;
}

export default function VolumeWindowControl({ studyData }: Props) {
  const viewerMode = useViewerStore((s) => s.viewerMode);
  const volumeBuildMode = useViewerStore((s) => s.volumeBuildMode);
  const setVolumeBuildMode = useViewerStore((s) => s.setVolumeBuildMode);
  const focusWindows = useViewerStore((s) => s.focusWindows);
  const setFocusWindow = useViewerStore((s) => s.setFocusWindow);

  // Largest series = the one setupVolumeViewports will actually render.
  const series = studyData?.series?.reduce<SeriesData | null>(
    (b, s) => (!b || s.numImages > b.numImages) ? s : b,
    null
  );

  const budget = estimateVolumeBudget();
  const total = series?.numImages ?? 0;
  const isOversized = total > budget.maxSlices;

  // Pending (unapplied) slider state — applied to the store when user hits Apply.
  const saved = series ? focusWindows[series.seriesId] : undefined;
  const [pendingStart, setPendingStart] = useState(saved?.start ?? 0);
  const [pendingEnd, setPendingEnd] = useState(
    saved?.end ?? Math.min(total, budget.maxSlices)
  );

  // Keep pending window in sync when the series changes.
  useEffect(() => {
    if (!series) return;
    const s = focusWindows[series.seriesId];
    setPendingStart(s?.start ?? 0);
    setPendingEnd(s?.end ?? Math.min(series.numImages, budget.maxSlices));
  }, [series?.seriesId, series?.numImages, budget.maxSlices]);

  if (viewerMode === 'stack' || !series || !isOversized) return null;

  const pendingSize = pendingEnd - pendingStart;
  const isWindowAtBudget = pendingSize >= budget.maxSlices;
  const applied = saved
    ? pendingStart === saved.start && pendingEnd === saved.end
    : (pendingStart === 0 && pendingEnd === Math.min(total, budget.maxSlices));

  function setMode(mode: VolumeBuildMode) {
    if (mode !== volumeBuildMode) setVolumeBuildMode(mode);
  }

  function handleStartChange(v: number) {
    let next = Math.max(0, Math.min(total - 1, v));
    let end = pendingEnd;
    if (end - next > budget.maxSlices) end = next + budget.maxSlices;
    if (end <= next) end = next + 1;
    setPendingStart(next);
    setPendingEnd(Math.min(total, end));
  }

  function handleEndChange(v: number) {
    let next = Math.max(pendingStart + 1, Math.min(total, v));
    let start = pendingStart;
    if (next - start > budget.maxSlices) start = next - budget.maxSlices;
    setPendingStart(Math.max(0, start));
    setPendingEnd(next);
  }

  function apply() {
    if (!series) return;
    setFocusWindow(series.seriesId, { start: pendingStart, end: pendingEnd });
  }

  return (
    <div className={styles.panel}>
      <div className={styles.row}>
        <div className={styles.modeGroup} title={`Budget: ${budget.maxSlices} slices (${budget.reason})`}>
          <button
            className={`${styles.modeBtn} ${volumeBuildMode === 'overview' ? styles.modeActive : ''}`}
            onClick={() => setMode('overview')}
          >
            Overview
          </button>
          <button
            className={`${styles.modeBtn} ${volumeBuildMode === 'focus' ? styles.modeActive : ''}`}
            onClick={() => setMode('focus')}
          >
            Focus
          </button>
        </div>

        {volumeBuildMode === 'overview' && (
          <div className={styles.info}>
            Middle {Math.min(budget.maxSlices, total)} slices of {total} · switch to Focus to pick a window
          </div>
        )}

        {volumeBuildMode === 'focus' && (
          <>
            <div className={styles.sliderBlock}>
              <label>
                <span className={styles.lbl}>Start</span>
                <input
                  type="number"
                  min={0}
                  max={total - 1}
                  value={pendingStart}
                  onChange={(e) => handleStartChange(Number(e.target.value))}
                />
              </label>
              <input
                type="range"
                min={0}
                max={total - 1}
                value={pendingStart}
                onChange={(e) => handleStartChange(Number(e.target.value))}
                className={styles.range}
              />
              <label>
                <span className={styles.lbl}>End</span>
                <input
                  type="number"
                  min={pendingStart + 1}
                  max={total}
                  value={pendingEnd}
                  onChange={(e) => handleEndChange(Number(e.target.value))}
                />
              </label>
              <input
                type="range"
                min={pendingStart + 1}
                max={total}
                value={pendingEnd}
                onChange={(e) => handleEndChange(Number(e.target.value))}
                className={styles.range}
              />
            </div>
            <div className={styles.info}>
              {pendingSize} slices
              {isWindowAtBudget && <span className={styles.warn}> (at GPU budget)</span>}
            </div>
            <button
              className={styles.applyBtn}
              onClick={apply}
              disabled={applied}
              title={applied ? 'Window already applied' : 'Rebuild volume with this window'}
            >
              {applied ? 'Applied' : 'Apply window'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
